import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Sparkles, ShieldAlert, RotateCcw, Lock, Share2 } from "lucide-react";
import { api } from "@/lib/api";
import { AppStoreBadges } from "@/components/AppStoreBadges";

export function TryScenario() {
  const [scenarios, setScenarios] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [remaining, setRemaining] = useState(5);
  const [limit, setLimit] = useState(false);
  const [safety, setSafety] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [fbLoading, setFbLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api("/demo/scenarios").then((d) => setScenarios(d.scenarios || [])).catch(() => {});
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending, fbLoading, feedback]);

  const userTurns = messages.filter((m) => m.sender === "user").length;

  const pick = (s) => {
    setActive(s);
    setMessages([{ sender: "ai", text: s.opening_line }]);
    setRemaining(5);
    setLimit(false);
    setSafety(null);
    setFeedback(null);
    setFbLoading(false);
    setInput("");
  };

  const reset = () => {
    setActive(null);
    setMessages([]);
    setLimit(false);
    setSafety(null);
    setFeedback(null);
    setFbLoading(false);
  };

  const getFeedback = async (msgs) => {
    const history = (msgs || messages).map((m) => ({ sender: m.sender, text: m.text }));
    setLimit(true);
    setFbLoading(true);
    try {
      const res = await api("/demo/feedback", { method: "POST", body: { scenario_id: active.id, history } });
      setFeedback(res.feedback);
    } catch (e) {
      setFeedback(null);
    } finally {
      setFbLoading(false);
    }
  };

  const shareScore = async () => {
    if (!feedback) return;
    const url = `${window.location.origin}/#try`;
    const text = `I scored ${feedback.score}/100 practicing "${active.title}" on Next Time. Handle it better next time — try a free role-play:`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "My Next Time score", text, url });
        return;
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast.success("Score copied, paste it anywhere to share");
    } catch {
      toast.error("Couldn't share right now");
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || sending || limit) return;
    setInput("");
    setSafety(null);
    const history = messages.map((m) => ({ sender: m.sender, text: m.text }));
    setMessages((m) => [...m, { sender: "user", text }]);
    setSending(true);
    try {
      const res = await api("/demo/reply", { method: "POST", body: { scenario_id: active.id, history, text } });
      if (res.safety) { setSafety(res.safety.message); return; }
      if (res.limit_reached && !res.reply) {
        getFeedback([...messages, { sender: "user", text }]);
        return;
      }
      const nextMsgs = res.reply ? [...messages, { sender: "user", text }, res.reply] : [...messages, { sender: "user", text }];
      if (res.reply) setMessages((m) => [...m, res.reply]);
      setRemaining(res.remaining ?? 0);
      if (res.limit_reached) getFeedback(nextMsgs);
    } catch (e) {
      setMessages((m) => [...m, { sender: "ai", text: "The AI is busy right now, give it another try in a moment." }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="try" className="bg-cream-alt border-y border-cardborder">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.15em] text-sage-hover">Try it now</p>
          <h2 className="mt-3 font-serif font-semibold tracking-tight text-3xl sm:text-4xl text-ink leading-tight">
            Run a real role-play, right here
          </h2>
          <p className="mt-4 text-lg text-ink-muted leading-relaxed">
            No signup. Pick a situation, and a live AI partner plays the other side, pushing back just like the real thing. It's a taste of what practice feels like.
          </p>
          <div className="mt-7 space-y-3">
            {scenarios.map((s) => (
              <button
                key={s.id}
                data-testid={`try-scenario-${s.id}`}
                onClick={() => pick(s)}
                className={`w-full text-left rounded-2xl border p-4 transition-all hover:-translate-y-0.5 ${
                  active?.id === s.id ? "bg-ink text-cream border-ink-soft" : "bg-cardbg border-cardborder hover:border-sage"
                }`}
              >
                <p className={`font-serif text-lg ${active?.id === s.id ? "text-cream" : "text-ink"}`}>{s.title}</p>
                <p className={`text-sm mt-1 ${active?.id === s.id ? "text-cream/70" : "text-ink-muted"}`}>{s.description}</p>
              </button>
            ))}
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <div className="rounded-3xl border-8 border-ink bg-ink p-2 shadow-[0_30px_80px_-20px_rgba(15,23,42,0.4)]">
            <div className="rounded-[1.4rem] bg-cream overflow-hidden flex flex-col h-[520px]">
              {!active ? (
                <div className="flex-1 grid place-items-center p-8 text-center">
                  <div>
                    <video
                      className="mx-auto w-40 h-40 object-cover"
                      style={{ WebkitMaskImage: "radial-gradient(circle at 50% 48%, #000 52%, transparent 74%)", maskImage: "radial-gradient(circle at 50% 48%, #000 52%, transparent 74%)" }}
                      src="/assets/mascot.mp4"
                      poster="/assets/mascot-poster.jpg"
                      autoPlay muted loop playsInline preload="auto"
                    />
                    <p className="mt-4 font-serif text-xl text-ink">Choose a situation to begin</p>
                    <p className="mt-2 text-sm text-ink-muted max-w-xs mx-auto">The AI stays in character. You get {5} replies to feel it out.</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="shrink-0 px-4 py-3 border-b border-cardborder flex items-center justify-between bg-cream/90">
                    <div className="min-w-0">
                      <p className="font-serif text-sm text-ink truncate">{active.title}</p>
                      <p className="text-[11px] text-ink-muted truncate">Talking to: {active.ai_role}</p>
                    </div>
                    <button onClick={reset} className="p-2 text-ink-muted hover:text-ink" title="Restart" data-testid="try-reset"><RotateCcw className="h-4 w-4" /></button>
                  </div>

                  <div ref={scrollRef} className="flex-1 overflow-y-auto nt-scroll px-4 py-4 space-y-3">
                    {messages.map((m, i) => (
                      <div key={i} className={`flex ${m.sender === "user" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[82%] p-3 text-sm leading-relaxed shadow-sm ${
                          m.sender === "user" ? "bg-ink text-cream rounded-2xl rounded-tr-sm" : "bg-cardbg text-ink border border-cardborder rounded-2xl rounded-tl-sm"
                        }`}>{m.text}</div>
                      </div>
                    ))}
                    {sending && (
                      <div className="flex justify-start">
                        <div className="bg-cardbg border border-cardborder rounded-2xl rounded-tl-sm p-3 flex gap-1.5">
                          {[0, 1, 2].map((i) => <span key={i} className="h-2 w-2 rounded-full bg-sage nt-blink" style={{ animationDelay: `${i * 0.2}s` }} />)}
                        </div>
                      </div>
                    )}
                    {safety && (
                      <div className="rounded-xl bg-clay-light border border-clay/30 p-3 text-xs text-clay flex gap-2">
                        <ShieldAlert className="h-4 w-4 shrink-0" /> <span>{safety}</span>
                      </div>
                    )}
                    {limit && (
                      fbLoading ? (
                        <div className="rounded-2xl bg-cardbg border border-cardborder p-6 text-center">
                          <div className="flex justify-center gap-1.5">
                            {[0, 1, 2].map((i) => <span key={i} className="h-2.5 w-2.5 rounded-full bg-sage nt-blink" style={{ animationDelay: `${i * 0.2}s` }} />)}
                          </div>
                          <p className="mt-3 text-sm text-ink-muted">Scoring how you did…</p>
                        </div>
                      ) : feedback ? (
                        <div className="rounded-2xl bg-ink text-cream p-5 nt-grain relative overflow-hidden" data-testid="try-scorecard">
                          <div className="relative">
                            <div className="flex items-center gap-4">
                              <div className="shrink-0 h-16 w-16 rounded-2xl bg-sage grid place-items-center">
                                <span className="font-serif text-2xl text-white leading-none">{feedback.score ?? "—"}</span>
                              </div>
                              <div>
                                <p className="font-mono text-[10px] uppercase tracking-widest text-sage-muted">Your demo score</p>
                                <p className="mt-1 text-sm text-cream/90 leading-snug">{feedback.overall}</p>
                              </div>
                            </div>
                            {feedback.did_well?.length > 0 && (
                              <div className="mt-4 flex flex-wrap gap-1.5">
                                {feedback.did_well.map((t, i) => (
                                  <span key={i} className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[11px] text-cream/90">✓ {t}</span>
                                ))}
                              </div>
                            )}
                            {feedback.one_tip && (
                              <div className="mt-3 rounded-xl bg-sage-light/95 text-sage-hover p-3 text-xs flex gap-2">
                                <Sparkles className="h-4 w-4 shrink-0" /><span><span className="font-semibold">Next time: </span>{feedback.one_tip}</span>
                              </div>
                            )}
                            <div className="mt-4 flex items-center gap-2">
                              <button data-testid="try-share-score" onClick={shareScore} className="shrink-0 inline-flex items-center justify-center gap-1.5 rounded-full bg-white/10 text-cream px-4 py-2.5 text-sm font-semibold hover:bg-white/20 transition-colors">
                                <Share2 className="h-4 w-4" /> Share my score
                              </button>
                            </div>
                            <div className="mt-4 border-t border-white/10 pt-4">
                              <p className="text-[11px] text-cream/60 text-center mb-3">Get the full experience, unlimited practice, daily scenarios and progress tracking</p>
                              <div className="flex justify-center"><AppStoreBadges dark /></div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-2xl bg-sage text-white p-5 text-center">
                          <Lock className="h-6 w-6 mx-auto" />
                          <p className="mt-3 font-serif text-lg">Nice, that's the demo!</p>
                          <p className="mt-1 text-sm text-white/85">Download Next Time to keep practicing with scored coaching feedback.</p>
                          <div className="mt-4 flex justify-center"><AppStoreBadges dark /></div>
                        </div>
                      )
                    )}
                  </div>

                  {!limit && (
                    <div className="shrink-0 px-4 py-3 border-t border-cardborder">
                      <div className="flex items-center gap-2">
                        <input
                          data-testid="try-input"
                          value={input}
                          onChange={(e) => setInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
                          placeholder="Type your reply…"
                          className="flex-1 rounded-full border border-cardborder bg-cardbg px-4 py-2.5 text-sm text-ink placeholder:text-ink-muted/60 outline-none focus:border-sage focus:ring-2 focus:ring-sage/20"
                        />
                        <button onClick={send} disabled={sending || !input.trim()} data-testid="try-send" className="h-10 w-10 shrink-0 grid place-items-center rounded-full bg-sage text-white hover:bg-sage-hover transition-colors disabled:opacity-50">
                          <Send className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="mt-2 flex items-center justify-between">
                        <p className="text-[11px] text-ink-muted">{remaining} {remaining === 1 ? "reply" : "replies"} left</p>
                        <button
                          data-testid="try-finish"
                          onClick={() => getFeedback()}
                          disabled={userTurns < 1 || sending}
                          className="text-[11px] font-semibold text-sage-hover hover:underline disabled:opacity-40 disabled:no-underline"
                        >
                          Finish & see my score →
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
